import { DragDropContext, DropResult, Droppable } from "@hello-pangea/dnd";
import { Add } from "@mui/icons-material";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { FormikErrors } from "formik";
import TimePicker from "react-time-picker";
import { Exercise } from "@readyfit-common/models";
import { formatTimeString } from "@readyfit-common/utils";
import { useAppSelector } from "src/store/hooks";
import { selectSelectedWorkout } from "src/store/reducers/training/trainingApiSlice";
import { WorkoutFormValues } from "../EditWorkoutModal/EditWorkoutModal";
import ExerciseFields from "./ExerciseFields";
import useStyles from "./WorkoutForm.style";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

interface FitnessWorkoutFormProps {
  values: WorkoutFormValues;
  errors: FormikErrors<WorkoutFormValues>;
  handleChange: (
    field: string,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: (field: string, value: any) => void;
}

export const StyledTimePickerWrapper = styled(Box)(({ theme }) => ({
  "& .react-time-picker": {
    width: "100%",
  },
  "& .react-time-picker__wrapper": {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: "8px 14px",
    height: "56px",
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
  },
  "& .react-time-picker__inputGroup": {
    fontSize: "1rem",
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    flex: 1,
  },
  "& .react-time-picker__button": {
    color: theme.palette.action.active,
    "&:hover": {
      backgroundColor: "transparent",
    },
    "& svg": {
      width: "1.2em",
      height: "1.2em",
    },
    "&.input": {
      minWidth: "1.5em",
    },
  },
  "& .react-time-picker--disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  "& .react-time-picker__inputGroup__input": {
    minWidth: "1em",
  },
}));

const FitnessWorkoutForm = ({
  values,
  errors,
  handleChange,
  setFieldValue,
}: FitnessWorkoutFormProps) => {
  const classes = useStyles();
  const selectedWorkout = useAppSelector(selectSelectedWorkout);

  const onDragEnd = async (result: DropResult) => {
    // Don't do anything if dropped outside the list
    if (!result.destination) return;

    // Don't do anything if dropped in the same position
    if (
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index === result.source.index
    ) {
      return;
    }

    const newExercisesOrder = Array.from(values.exercises || []);
    const [reorderedItem] = newExercisesOrder.splice(result.source.index, 1);
    newExercisesOrder.splice(result.destination.index, 0, reorderedItem);
    setFieldValue("exercises", newExercisesOrder);
  };

  const handleAddExercise = () => {
    const newExercise: Partial<Exercise> = {
      name: "New Exercise",
      sets: "",
      reps: "",
      description: "",
    };

    setFieldValue("exercises", [...(values.exercises || []), newExercise]);
  };

  return (
    <>
      <Grid xs={12}>
        <InputLabel htmlFor="estimatedTime" className={classes.inputLabel}>
          <Typography variant="subtitle1">Estimated Time</Typography>
        </InputLabel>
        <StyledTimePickerWrapper>
          <TimePicker
            onChange={(value) => {
              console.log("value", value);
              if (value) {
                setFieldValue("estimatedTime", value.toString());
              }
            }}
            value={formatTimeString(values.estimatedTime || "")}
            format="HH:mm"
            clearIcon={null}
            disableClock={true}
          />
        </StyledTimePickerWrapper>
        {errors.estimatedTime && (
          <Typography variant="caption" color="error">
            {errors.estimatedTime}
          </Typography>
        )}
      </Grid>
      <Grid xs={12}>
        <InputLabel htmlFor="name" className={classes.inputLabel}>
          <Typography variant="subtitle1">Description</Typography>
        </InputLabel>
        <TextField
          multiline
          rows={4}
          fullWidth
          variant="outlined"
          value={values.description}
          placeholder="Description"
          error={!!errors.description}
          helperText={errors.description}
          InputProps={{
            className: classes.input,
          }}
          onChange={(e) =>
            handleChange("description")(
              e as React.ChangeEvent<HTMLInputElement>,
            )
          }
        />
      </Grid>

      <Box sx={{ width: "100%" }}>
        <Divider />
      </Box>

      <Grid container rowGap={2}>
        <Grid container item alignItems={"center"}>
          <Typography variant="h5" fontSize={18}>
            Exercises
          </Typography>
          <IconButton onClick={handleAddExercise}>
            <Add />
          </IconButton>
        </Grid>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="exercises" key={selectedWorkout?.id}>
            {(provided, snapshot) => {
              return (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{
                    position: "relative",
                    width: "100%",
                    minHeight: snapshot.isDraggingOver ? "100px" : "auto",
                    transition: "background-color 0.2s ease",
                    backgroundColor: snapshot.isDraggingOver
                      ? "rgba(0, 0, 0, 0.05)"
                      : "transparent",
                  }}
                >
                  <Grid container rowGap={1}>
                    {values?.exercises?.map(
                      (exercise: Exercise, index: number) => {
                        return (
                          <ExerciseFields
                            key={exercise.id}
                            index={index}
                            exercise={exercise}
                            values={values}
                            errors={errors}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                          />
                        );
                      },
                    )}
                    {provided.placeholder}
                  </Grid>
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>
      </Grid>
    </>
  );
};

export default FitnessWorkoutForm;
